import { SupportedLanguage } from '@/libs/sdk/api';
import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns/format';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import { intlFormatDistance } from 'date-fns/intlFormatDistance';
import { ar, enUS, es, ja, ko, pt, ru, vi, zhCN, zhHK } from 'date-fns/locale';
import type { BusinessDay, Time, UTCTimestamp } from 'lightweight-charts';

// Inlined from lightweight-charts so this eagerly-loaded util keeps only a
// type-level dependency on the chart library (its runtime stays lazy):
// a Time is a UTCTimestamp (number), a BusinessDay (object) or a date string.
const isUTCTimestamp = (t: Time): t is UTCTimestamp => typeof t === 'number';
const isBusinessDay = (t: Time): t is BusinessDay => typeof t !== 'number' && typeof t !== 'string';

export const formatAddress = (address?: string | null) => {
  if (!address) return '';
  return address.slice(0, 6) + '...' + address.slice(-4);
};

export const formatTimeToDate = (t: Time): Date => {
  if (isUTCTimestamp(t) || t.toString().length == 10) return new Date(Number(t) * 1000);
  if (isBusinessDay(t)) return new Date(t.year, t.month, t.day);
  const [year, month, day] = t.split('-').map(i => parseInt(i));
  return new Date(year, month, day);
};

// date-fns locale objects for absolute date formatting, keyed by app locale.
// `ar` is the generic (non-regional) Arabic locale, not `ar-SA`: date-fns'
// ar-SA data truncates the abbreviated month token (e.g. "أغسـ" for August)
// mid-word, which Arabic doesn't do — MSA month names aren't abbreviated, so
// the generic locale's un-abbreviated "أغسطس" is the correct rendering here.
const DATE_FNS_LOCALES: Record<string, typeof enUS> = {
  en: enUS,
  ar,
  'zh-cn': zhCN,
  'zh-hk': zhHK,
  es,
  ja,
  vi,
  ko,
  ru,
  pt,
};
// BCP-47 tags for Intl relative-time formatting (intlFormatDistance).
const INTL_LOCALES: Record<string, string> = {
  en: 'en-US',
  ar: 'ar-AE',
  'zh-cn': 'zh-CN',
  'zh-hk': 'zh-HK',
  es: 'es-ES',
  ja: 'ja-JP',
  vi: 'vi-VN',
  ko: 'ko-KR',
  ru: 'ru-RU',
  pt: 'pt-BR',
};

// Resolve an app locale (e.g. zh-cn, zh-tw, en-gb) to a date-fns locale, falling
// back by language family — zh* is simplified unless HK/TW — then to English.
const resolveDateFnsLocale = (language = 'en') => {
  const l = language.toLowerCase();
  if (DATE_FNS_LOCALES[l]) return DATE_FNS_LOCALES[l];
  if (l.startsWith('zh')) return l.includes('hk') || l.includes('tw') ? zhHK : zhCN;
  return DATE_FNS_LOCALES[l.split('-')[0]] ?? enUS;
};

const resolveIntlLocale = (language = 'en') => {
  const l = language.toLowerCase();
  if (INTL_LOCALES[l]) return INTL_LOCALES[l];
  if (l.startsWith('zh')) return l.includes('hk') || l.includes('tw') ? 'zh-HK' : 'zh-CN';
  return INTL_LOCALES[l.split('-')[0]] ?? 'en-US';
};

// `timeZone` (IANA, e.g. 'Asia/Singapore') pins the rendered wall-clock to that zone
// instead of the viewer's local one. Only pass it when the UI also *labels* the zone —
// an unlabelled foreign-zone timestamp is worse than a local one. Omit = viewer-local
// (the right default for "5 min ago"-style relative tooltips).
export const formatDate = (
  time: Time | Date,
  language: SupportedLanguage = 'en',
  formatStr = 'MMM d, yyyy h:mm a',
  timeZone?: string
) => {
  const d = time instanceof Date ? time : formatTimeToDate(time);
  return format(timeZone ? new TZDate(d.getTime(), timeZone) : d, formatStr, {
    locale: resolveDateFnsLocale(language),
  });
};

// format date as relative time (e.g. "5 minutes ago")
export const formatAfterDate = (time: Time | Date, language: SupportedLanguage = 'en', earlierDate = new Date()) => {
  return intlFormatDistance(time instanceof Date ? time : formatTimeToDate(time), earlierDate, {
    locale: resolveIntlLocale(language),
  });
};

export const formatHMS = (ms: number) => {
  const { hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({ start: 0, end: ms });
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}h:${pad(minutes)}m:${pad(seconds)}s`;
};

export const parseNumber = (value: any, decimal = 0, precision = 2) => {
  const numberValue = Number(value);
  if (!value || isNaN(numberValue)) return 0;
  const fixedDecimal = Math.max(0, decimal);
  const isNoPrecision = precision < 0;
  const numValue = isNoPrecision
    ? numberValue / Math.pow(10, fixedDecimal)
    : Math.floor(numberValue / Math.pow(10, fixedDecimal - precision)) / Math.pow(10, precision);
  return numValue;
};

export const formatNumber = (value: any, decimal: number = 0, precision: number = 0, separator = ',', compact = true) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '0';
  }
  decimal = Math.max(0, decimal);
  precision = Math.max(0, precision);
  const numValue = Math.floor(Number(value) / Math.pow(10, decimal - precision)) / Math.pow(10, precision);
  // Only use compact notation (M/B/T) for millions and above, show full number for thousands
  if (compact && Math.abs(numValue) >= 1_000_000) {
    const suffixes = ['', '', 'M', 'B', 'T']; // Skip K suffix, start from M
    const suffixIndex = Math.min(Math.floor(Math.log10(Math.abs(numValue)) / 3), suffixes.length - 1);
    const shortValue = numValue / Math.pow(1000, suffixIndex);
    const formatted = shortValue.toFixed(1).replace(/\.0$/, '');
    return `${formatted}${suffixes[suffixIndex]}`;
  }
  const parts = numValue.toString().split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const formatCurrency = (value: any, decimal: number = 6, precision: number = 2) => {
  return formatNumber(value, decimal, precision);
};

export const formatRate = (value: any, decimal: number = 0, precision: number = 2) => {
  return formatNumber(value, decimal, precision) + '%';
};

export const formatFirstUpperCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
